// src/supabaseClient.ts
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../types/database.types';
import { queryClient } from './constants';
import { createAuthQueryCacheInvalidator } from './authQueryCache';
import { clearImpersonationRecoveryState } from './impersonationStorage';

const SUPABASE_URL: string = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY: string = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_KEY) {
  throw new Error('Missing Supabase environment variables. Please check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set.');
}

/**
 * The localStorage key auth-js keeps the session under. supabase-js derives exactly
 * this string when no storageKey is given, so naming it here changes nothing at
 * runtime — it just makes the key a supported export instead of a value other
 * modules would have to re-derive or read off client internals. Impersonation
 * restores a session by writing this key directly (see utils/impersonation.ts).
 */
export const AUTH_STORAGE_KEY = `sb-${new URL(SUPABASE_URL).hostname.split('.')[0]}-auth-token`;

// Single global client with full database type safety
export const supabaseClient: SupabaseClient<Database> = createClient<Database>(SUPABASE_URL, SUPABASE_KEY, {
  auth: { storageKey: AUTH_STORAGE_KEY }
});

// Core CRM schema client for accessing core_crm functions and tables
// Shares auth automatically with supabaseClient via localStorage
export const supabaseCoreClient: SupabaseClient<Database, 'core_crm'> = createClient<Database, 'core_crm'>(SUPABASE_URL, SUPABASE_KEY, {
  db: { schema: 'core_crm' },
  auth: { storageKey: AUTH_STORAGE_KEY }
});

const invalidateAuthQueryCache = createAuthQueryCacheInvalidator(queryClient);
supabaseClient.auth.onAuthStateChange((event, session) => {
  invalidateAuthQueryCache(session?.access_token ?? null);
  if (event === 'SIGNED_OUT') {
    clearImpersonationRecoveryState();
  }
});

// Legacy export for debugging - extend window interface
declare global {
  interface Window {
    createClient: typeof createClient;
    supabaseClient: SupabaseClient<Database>; // Match our actual Database type
  }
}

if (typeof window !== 'undefined') {
  window.createClient = createClient;
  window.supabaseClient = supabaseClient;
}

/**
 * Validates SQL identifier input to ensure it follows PostgreSQL naming conventions
 * and security best practices.
 * 
 * @param sqlInput - The input string to validate
 * @param context - Context description for error messages (e.g., 'table name', 'field name')
 * @throws {Error} When input is invalid
 */
export const validateSqlIdentifierInput = (sqlInput: unknown, context: string): void => {
    const sampleGoodInput = 'the_name_of_your_thing';
    const requirements = `Only lowercase letters, numbers, and underscores are allowed, with a maximum length of 64 characters. Example of a good value: ${sampleGoodInput}`;

    if (!sqlInput || typeof sqlInput !== 'string') {
        throw new Error(`Invalid ${context}: "${String(sqlInput)}". Cannot be null or empty. ${requirements}`);
    }

    if (/\s/.test(sqlInput)) {
        throw new Error(`Invalid ${context}: "${sqlInput}". Cannot contain spaces. ${requirements}`);
    }

    if (!/^[a-z0-9_]+$/.test(sqlInput)) {
        throw new Error(`Invalid ${context}: "${sqlInput}". Input contains invalid characters. ${requirements}`);
    }

    if (sqlInput !== sqlInput.toLowerCase()) {
        throw new Error(`Invalid ${context}: "${sqlInput}". Input must be lowercase. ${requirements}`);
    }

    if (sqlInput.length > 64) {
        throw new Error(`Invalid ${context}: "${sqlInput}". Input exceeds the maximum length. ${requirements}`);
    }
}
